import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  OpenInNew as ExternalLinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { truncateText } from '../../utils/formatters';
const AnnouncementCard = memo(({ 
  announcement, 
  isAdmin,
  onViewDetails, 
  onEdit,
  onDelete,
  onDownloadAttachment,
  formatDate,
  variant = 'list'
}) => {
  const isGrid = variant === 'grid';
  // Only memoize expensive calculations
  const contentPreview = useMemo(() => 
    truncateText(announcement.content, isGrid ? 100 : 150), 
    [announcement.content, isGrid]
  );
  const formattedDate = useMemo(() => 
    formatDate(announcement.createdAt), 
    [announcement.createdAt, formatDate]
  );
  const formattedStartDate = useMemo(() => 
    announcement.startDate ? formatDate(announcement.startDate) : null, 
    [announcement.startDate, formatDate]
  );
  const formattedEndDate = useMemo(() => 
    announcement.endDate ? formatDate(announcement.endDate) : null, 
    [announcement.endDate, formatDate]
  );
  const formattedCreatedAt = useMemo(() => 
    announcement.createdAt ? formatDate(announcement.createdAt) : null, 
    [announcement.createdAt, formatDate]
  );
  const formattedUpdatedAt = useMemo(() => 
    announcement.updatedAt ? formatDate(announcement.updatedAt) : null, 
    [announcement.updatedAt, formatDate]
  );  
  const hasAttachment = useMemo(() => 
    announcement.mediaUrl && announcement.mediaUrl.trim() !== '', 
    [announcement.mediaUrl]
  );
  // Simplified styles without excessive memoization
  const cardStyles = {
    p: 2,
    backgroundColor: 'white',
    borderRadius: 2,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    '&:hover': {
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      transform: 'translateY(-1px)',
      transition: 'all 0.2s ease-in-out'
    },
    ...(isGrid && {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mb: 0
    }),
    ...(!isGrid && {
      mb: 1.5,
      '&:last-child': { mb: 0 }
    })
  };
  const contentStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...(isGrid && {
      flex: 1,
      flexDirection: 'column'
    })
  };
  const textContentStyles = {
    flex: 1
  };
  const buttonContainerStyles = {
    display: 'flex',
    gap: 1,
    ...(isGrid ? {
      mt: 'auto',
      justifyContent: 'center'
    } : {
      flexDirection: 'column',
      alignItems: 'flex-end',
      ml: 2
    })
  };
  const titleStyles = {
    fontWeight: 600, 
    mb: 1, 
    color: '#374151',
    fontSize: '1rem',
    lineHeight: isGrid ? 1.4 : 1.2
  };
  const contentTextStyles = {
    color: '#6b7280', 
    mb: 2, 
    lineHeight: 1.5,
    fontSize: '0.875rem',
    ...(isGrid && { flex: 1 })
  };
  const dateStyles = {
    color: '#9ca3af', 
    fontSize: '0.75rem',
    fontWeight: 500
  };
  const adminActionsStyles = {
    display: 'flex', 
    gap: 0.5, 
    mb: 1,
    justifyContent: isGrid ? 'center' : 'flex-end'
  };
  const iconButtonStyles = {
    color: '#6b7280',
    '&:hover': {
      backgroundColor: 'rgba(107, 114, 128, 0.1)',
      color: '#374151'
    }
  };
  const viewDetailsButtonStyles = {
    borderColor: '#F1AC2F',
    color: '#F1AC2F',
    backgroundColor: 'white',
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 1,
    px: isGrid ? 1.5 : 2,
    py: 0.5,
    fontSize: '0.75rem',
    minWidth: isGrid ? 'auto' : '90px',
    flex: isGrid ? 1 : 'none',
    '&:hover': { 
      backgroundColor: '#F1AC2F',
      color: 'white',
      borderColor: '#F1AC2F'
    }
  };
  const attachmentButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: '#3b82f6',
    textDecoration: 'underline',
    fontSize: '0.75rem',
    textTransform: 'none',
    p: 0,
    minWidth: 'auto',
    fontWeight: 500,
    '&:hover': { 
      color: '#2563eb',
      backgroundColor: 'transparent'
    }
  };
  return (
    <Paper 
      sx={cardStyles}
      role="article"
      aria-labelledby={`announcement-title-${announcement.id}`}
    >
      <Box sx={contentStyles}>
        <Box sx={textContentStyles}>
          <Typography 
            variant="h6" 
            id={`announcement-title-${announcement.id}`}
            sx={titleStyles}
          >
            {announcement.title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={contentTextStyles}
            aria-describedby={`announcement-content-${announcement.id}`}
          >
            {contentPreview}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 0.5,
            mb: isGrid ? 2 : 0 
          }}>
            {/* Start Date and End Date side by side - Only for admin */}
            {isAdmin && (formattedStartDate || formattedEndDate) && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                flexWrap: 'wrap'
              }}>
                {/* Start Date */}
                {formattedStartDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                    <Typography 
                      variant="body2" 
                      sx={dateStyles}
                      aria-label={`Start Date: ${formattedStartDate}`}
                    >
                      Start Date: {formattedStartDate}
                    </Typography>
                  </Box>
                )}
                {/* End Date */}
                {formattedEndDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                    <Typography 
                      variant="body2" 
                      sx={dateStyles}
                      aria-label={`End Date: ${formattedEndDate}`}
                    >
                      End Date: {formattedEndDate}
                    </Typography>
                  </Box>
                )}
                {/* Created At */}
                {formattedCreatedAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                    <Typography 
                      variant="body2" 
                      sx={dateStyles}
                      aria-label={`Created Date: ${formattedCreatedAt}`}
                    >
                      Created Date: {formattedCreatedAt}
                    </Typography>
                  </Box>
                )}
                {/* Updated At */}
                {formattedUpdatedAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                    <Typography 
                      variant="body2" 
                      sx={dateStyles}
                      aria-label={`Updated Date: ${formattedUpdatedAt}`}
                    >
                      Updated Date: {formattedUpdatedAt}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={buttonContainerStyles}>
          {/* Admin Actions */}
          {isAdmin && (
            <Box sx={adminActionsStyles}>
              <IconButton
                size="small"
                onClick={() => onEdit?.(announcement)}
                sx={iconButtonStyles}
                aria-label={`Edit announcement: ${announcement.title}`}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onDelete?.(announcement.id)}
                sx={iconButtonStyles}
                aria-label={`Delete announcement: ${announcement.title}`}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => onViewDetails(announcement)}
            sx={viewDetailsButtonStyles}
            aria-label={`View details for: ${announcement.title}`}
          >
            View Details
          </Button>
          {hasAttachment && (
            <Button
              variant="text"
              size="small"
              onClick={() => onDownloadAttachment(announcement.mediaUrl)}
              sx={attachmentButtonStyles}
              aria-label={`Download attachment for: ${announcement.title}`}
            >
              Attachment
              <ExternalLinkIcon sx={{ fontSize: 12 }} />
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
});
AnnouncementCard.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    mediaUrl: PropTypes.string,
    originalFilename: PropTypes.string
  }).isRequired,
  isAdmin: PropTypes.bool,
  onViewDetails: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDownloadAttachment: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['list', 'grid'])
};
AnnouncementCard.defaultProps = {
  isAdmin: false,
  onEdit: null,
  onDelete: null,
  variant: 'list'
};
AnnouncementCard.displayName = 'AnnouncementCard';
export default AnnouncementCard; 